import { isSchemaValidationError, type ApiError } from '@brightlocal/api-client';
import type { LocationId } from '@brightlocal/shared';

import { apiClient } from 'shared/apiClient';
import { getAccessToken } from 'shared/services';
import { captureSchemaValidationError, type Feature } from 'shared/utils';

import { LOCATION_FEATURE_TAG } from '../consts';

interface FetchLocationParams {
  /** Numeric location id. */
  locationId: LocationId;
  /** Abort signal from TanStack Query. */
  signal: AbortSignal;
  /** Sentry feature tag for schema validation errors. */
  sentryFeature?: Feature;
  /** When true, returns null instead of throwing on recoverable errors. */
  softFail?: boolean;
  /** Called once per soft-fail before returning null for non-schema errors. */
  onRecoverableError?: (error: unknown) => void;
}

/** Fetches a single location — shared by loaders, hooks, and polling. */
export const fetchLocation = async ({
  locationId,
  signal,
  sentryFeature = LOCATION_FEATURE_TAG,
  softFail = false,
  onRecoverableError
}: FetchLocationParams) => {
  try {
    return await apiClient.locations.getLocation(locationId, { signal });
  } catch (error) {
    if (softFail && !getAccessToken()) {
      return null;
    }

    if (isSchemaValidationError(error as ApiError)) {
      captureSchemaValidationError({
        feature: sentryFeature,
        error,
        method: 'apiClient.locations.getLocation'
      });
    }

    if (softFail) {
      if (!isSchemaValidationError(error as ApiError)) {
        onRecoverableError?.(error);
      }
      return null;
    }

    throw error;
  }
};
